import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { journalLines, type BookingPaid, type JournalLine } from '@lemontaigu/pms-core';
import {
  VEGA_HELD_TITLE_FR,
  PMS_ACTION_LABELS_FR,
  PMS_SOURCE_LABELS_FR,
  PMS_STATUS_LABELS_FR,
  pmsActionIsBookingWide,
  pmsActionNeedsReason,
  pmsActionsFrom,
  pmsLiveRoomCount,
  pmsPartialStatusLabelFr,
  pmsEditRule,
  pmsIsEditable,
  pmsNightCount,
  transferOverdue,
  type PmsReservationStatus,
  type PmsTransitionAction,
} from '@lemontaigu/pms-core';
import type {
  Guest,
  Product,
  RatePlan,
  ReservationView,
  Room,
  RoomStayView,
  RoomType,
} from '@lemontaigu/pms-core';
import { usePms, usePmsReach } from './PmsProvider.js';
import { PAYMENT_STATE_LABEL_FR, paymentState, type AdvanceRead } from './advanceView.js';
import { billedLines, type BilledLine } from './billedLines.js';
import type { PlanGapRead } from './planGapView.js';
import { mayLeave, useWarnBeforeUnload } from './leaveGuard.js';
import { EtablissementTag } from './EtablissementTag.js';
import { ReservationForm } from './ReservationForm.js';
import { CancelDialog, type CancelChoice, type CancelDialogAction } from './CancelDialog.js';
import { FolioDialog } from './FolioDialog.js';
import { Turnaways } from './Turnaways.js';
import { TrainingReset } from './TrainingReset.js';
import { DayPassage } from './DayPassage.js';
import { PendingConfirmations } from './PendingConfirmations.js';
import { CallFlow } from './call/CallFlow.js';
import { callButtonLabel } from './call/callModel.js';
import {
  buildReservationWrite,
  emptyForm,
  formFromReservation,
  withChargeDays,
  withPersons,
  type FormState,
} from './reservationDraft.js';
import {
  PMS_FILTER_LABELS,
  filterReservations,
  bookingOverdue,
  OVERDUE_LABELS_FR,
  propertyModes,
  rowIsVegaHeld,
  fmtDate,
  fmtEuros,
  isoAddDays,
  parseEurosToCents,
  sortByRoomCategory,
  type PmsFilter,
  defaultPlanFor,
} from './logic.js';

// « Réservations » (PMS P1, bos.*) — manual reservation CRUD in SHADOW mode:
// the operator mirrors real Vega/Amenitiz bookings by hand (PMS-BRIEF §4 P1).
// All writes go through the pms_* RPCs; transitions via pms_reservation_transition.

export function Reservations() {
  const pms = usePms();
  // Needs and allergens are health data (group edit step 5): read, shown and
  // sent only by a desk holding their own capability.
  const canNeeds = useCapability(CAPABILITIES.PMS_NEEDS_READ);
  /** The « Paiement » cell: the status, and what was paid of the total on hover.
   *  « … » while reading; nothing for a cancelled booking (no payment is due). */
  const paymentCell = (r: ReservationView) => {
    if (r.status === 'cancelled') return '—';
    if (paid === null || paid === 'off') return '…';
    const p = paid.get(r.id);
    if (p === undefined) return '—';
    const state = paymentState(p.paidCents, r.totalCents);
    if (state === null) return '—';
    const title = `Payé ${fmtEuros(p.paidCents)} sur ${fmtEuros(r.totalCents)}${
      p.arrhesCents !== 0 ? ` · dont arrhes ${fmtEuros(p.arrhesCents)}` : ''
    }${state === 'partial' ? ` · reste ${fmtEuros(r.totalCents - p.paidCents)}` : ''}`;
    return (
      <span className={`pms-pay ${state === 'paid' ? 'is-ok' : 'is-warn'}`} title={title}>
        {PAYMENT_STATE_LABEL_FR[state]}
      </span>
    );
  };

  const todayIso = useMemo(() => todayIsoParis(), []);
  const [reservations, setReservations] = useState<ReservationView[] | null>(null);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [ratePlans, setRatePlans] = useState<RatePlan[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [stays, setStays] = useState<RoomStayView[]>([]);
  const [filter, setFilter] = useState<PmsFilter>('a_venir');
  // OB1: « Réservations PMS » — only the bookings the PMS owns, on top of the status filter.
  const [pmsOnly, setPmsOnly] = useState(false);
  // OB2 (Q1): the open booking is Vega's — the form opens read-only.
  const [formHeld, setFormHeld] = useState(false);
  // OB3: « Annuler » / « No-show » open a dialog with the reason, the terms and the fee.
  // `stay` set = a no-show on one logement of a multi-room booking.
  // OB6: the open booking's « Note » — folios, payments, factures.
  const [folioFor, setFolioFor] = useState<ReservationView | null>(null);
  const [act, setAct] = useState<{ action: CancelDialogAction; booking: ReservationView; stay: RoomStayView | null } | null>(
    null,
  );
  const [search, setSearch] = useState('');
  const [err, setErr] = useState('');
  const [form, setForm] = useState<FormState | null>(null);
  // PB5: the établissement a phone call is being taken for — the « Appel » screen replaces the list.
  const [callFor, setCallFor] = useState<string | null>(null);
  // U3: the établissement the open form belongs to — the booking's own, or
  // the unit a cell was clicked on; null for « Nouvelle », where the form asks.
  const [formProperty, setFormProperty] = useState<string | null>(null);
  // The open booking's arrhes, read with the rest of what the form opens with. A
  // failed read does not stop the form opening: it is shown, never edited.
  const [formAdvance, setFormAdvance] = useState<AdvanceRead>(null);
  // Where Vega's charges and its rooming list disagree for the open booking (planGapView).
  const [formPlanGap, setFormPlanGap] = useState<PlanGapRead>(null);
  // The booking's whole-booking lines as billed (breakfast at 9,00 where the catalogue says 11,00).
  const [formBilled, setFormBilled] = useState<BilledLine[]>([]);
  // The open booking's « Journal » (O5): who did what, « par Marie D. ».
  const [formJournal, setFormJournal] = useState<JournalLine[]>([]);
  // Whether the desk has changed anything in the open form (the form reports it).
  const [formDirty, setFormDirty] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const reach = usePmsReach();
  // OB1: who holds a booking turns on its établissement's mode (Vega's until it is live).
  const modes = useMemo(() => propertyModes(reach.map((b) => b.property)), [reach]);
  // A booking is read and written through ITS établissement's services: the
  // screen's scoped ones would read nothing of another building's booking.
  const servicesFor = (code: string | null) => reach.find((b) => b.property.code === code)?.services ?? pms;
  const etablissementLabels = Object.fromEntries(reach.map((b) => [b.property.code, b.property.labelFr]));
  const [saving, setSaving] = useState(false);

  // U3: one list of every établissement in reach, each read through its own
  // bundle — or, without a property list, the scoped services as before.
  const load = useCallback(async () => {
    setErr('');
    const sources = reach.length > 0 ? reach.map((b) => b.services) : [pms];
    const per = await Promise.all(
      sources.map((svc) =>
        Promise.all([
          svc.reservations.list(),
          svc.reservations.listAllRoomStays(),
          svc.reference.roomTypes(),
          svc.reference.rooms(),
          svc.reference.ratePlans(),
          svc.reference.products(),
        ]),
      ),
    );
    const bad = per.flat().find((r) => !r.ok);
    if (bad && !bad.ok) {
      setErr(bad.error.message);
      return;
    }
    const all = <T,>(i: number) =>
      per.flatMap((p) => {
        const r = p[i]!;
        return r.ok ? (r.value as T[]) : [];
      });
    setReservations(all<ReservationView>(0));
    setStays(all<RoomStayView>(1));
    // Same canonical order as Calendrier and Configuration — the category
    // select must not read differently depending on which tab you are on.
    setRoomTypes(sortByRoomCategory(all<RoomType>(2)));
    setRooms(all<Room>(3));
    setRatePlans(all<RatePlan>(4));
    setProducts(all<Product>(5));
  }, [pms, reach]);

  useEffect(() => {
    void load();
  }, [load]);

  const typeById = useMemo(() => new Map(roomTypes.map((t) => [t.id, t])), [roomTypes]);

  // Logements indexed by their booking. `listAllRoomStays` returns LIVE ones,
  // so a released room is absent here and the list simply stops counting it —
  // which is what « if one room is released, it is available » means for a
  // desk reading the row.
  const staysOf = useMemo(() => {
    const m = new Map<string, RoomStayView[]>();
    for (const s of stays) {
      const list = m.get(s.reservationId);
      if (list === undefined) m.set(s.reservationId, [s]);
      else list.push(s);
    }
    return m;
  }, [stays]);
  const overdueCtx = useMemo(() => ({ modes, staysOf }), [modes, staysOf]);
  const visible = filterReservations(reservations ?? [], filter, todayIso, search, pmsOnly ? modes : null, overdueCtx);
  // OB4: the « À traiter » chip's count — whatever the other filters say.
  const overdueCount = useMemo(
    () => filterReservations(reservations ?? [], 'a_traiter', todayIso, '', null, overdueCtx).length,
    [reservations, todayIso, overdueCtx],
  );

  // « Paiement »: what each LISTED booking has paid, in one call per établissement
  // (not one per row). A desk without the payment capability, or a failed read,
  // shows no column at all rather than a wrong one.
  const [paid, setPaid] = useState<ReadonlyMap<string, BookingPaid> | 'off' | null>(null);
  const visibleKey = visible.map((r) => r.id).join(',');
  useEffect(() => {
    if (visibleKey === '') return;
    let cancelled = false;
    const byEtab = new Map<string, string[]>();
    for (const r of visible) byEtab.set(r.propertyCode, [...(byEtab.get(r.propertyCode) ?? []), r.id]);
    void Promise.all([...byEtab].map(([code, ids]) => servicesFor(code).arrivals.paidTotals(ids))).then((all) => {
      if (cancelled) return;
      if (!all.every((x) => x.ok)) return setPaid('off');
      setPaid(new Map(all.flatMap((x) => (x.ok ? x.value : [])).map((p) => [p.reservationId, p])));
    });
    return () => {
      cancelled = true;
    };
    // `visible` is what visibleKey names; servicesFor reads the reach.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey, reach, pms]);
  const showPay = paid !== 'off';
  const cols = showPay ? 10 : 9;

  const openNew = () => {
    setFormProperty(null);
    setFormHeld(false);
    setFormAdvance(null);
    setFormPlanGap(null);
    setFormBilled([]);
    setFormJournal([]);
    setFormDirty(false);
    setForm(emptyForm(todayIso));
  };

// Opening an edit loads the stay's CHARGE LINES, not just the reservation.
  // Two reasons, and the second is the one that bites: the per-night prices are
  // only in the lines (the reservation carries a total), and `p_products` is
  // read by the writer as the COMPLETE set of extras — so a form that saved
  // without having loaded them would silently drop the guest's « animal ».
  //
  // Since 6b it also loads the booking's LOGEMENTS, and they are awaited before
  // the form opens rather than filled in behind it: the form IS the room list
  // now, so showing it early would show a booking with no rooms and let the
  // desk start typing into one that is about to be replaced.
  const openEdit = async (r: ReservationView) => {
    // EVERYTHING the form edits is read before it opens: the logements, their
    // charge lines (the per-night prices, and the extras `p_products` must get
    // back complete), the persons, their meal exceptions and — for a desk
    // that may see them — their needs. The form takes its draft once — a read
    // landing after it opened would never reach it, and a save would then drop
    // what it had not loaded.
    const svc = servicesFor(r.propertyCode);
    const [stays, lines, persons, meals, needs, advance, gap, history] = await Promise.all([
      svc.reservations.roomStays(r.id),
      svc.reservations.chargeDays(r.id),
      svc.reservations.persons(r.id),
      svc.reservations.personMeals(r.id),
      canNeeds ? svc.reservations.personNeeds(r.id) : Promise.resolve(null),
      svc.arrivals.advance(r.id),
      svc.arrivals.planGap(r.id),
      svc.reservations.history(r.id),
    ]);
    for (const x of [stays, lines, persons, meals, needs]) {
      if (x !== null && !x.ok) {
        setErr(x.error.message);
        return;
      }
    }
    if (!stays.ok || !lines.ok || !persons.ok || !meals.ok || (needs !== null && !needs.ok)) return;
    setFormProperty(r.propertyCode);
    setFormHeld(rowIsVegaHeld(modes, r));
    setFormAdvance(advance.ok ? advance.value : advance.error.message);
    setFormPlanGap(gap.ok ? gap.value : gap.error.message);
    setFormBilled(billedLines(lines.value, products));
    // A failed read only hides the journal: it is a reading, never edited.
    setFormJournal(history.ok ? journalLines(history.value) : []);
    setForm(
      withPersons(
        withChargeDays(formFromReservation(r, stays.value), lines.value, products),
        persons.value,
        meals.value,
        needs === null ? undefined : needs.value,
      ),
    );
  };

  const save = async (f: FormState, propertyCode: string | null) => {
    const svc = servicesFor(propertyCode);
    const built = buildReservationWrite(f, defaultPlanFor(ratePlans, propertyCode));
    if (!built.ok) {
      setErr(built.error);
      return;
    }
    setSaving(true);
    setErr('');
    const { guest, fields, status } = built.write;
    const result =
      f.id === null
        ? await svc.reservations.book(guest, { ...fields, status, source: 'manual' })
        : await svc.reservations.update(f.id, guest, fields);
    setSaving(false);
    if (!result.ok) {
      setErr(result.error.message);
      return;
    }
    setForm(null);
    setFormDirty(false);
    await load();
  };

  // Same behaviour as the planning's panel: leaving the form over typed work asks
  // first; a click anywhere outside it closes it when nothing was changed. The
  // form is inline here, so « outside » is any click off its own box. Captured,
  // so a refusal also stops the click from doing what it was aimed at (opening
  // another booking over the one being edited).
  const closeForm = () => {
    if (!mayLeave(formDirty)) return;
    setForm(null);
    setFormDirty(false);
  };
  useWarnBeforeUnload(form !== null && formDirty);
  useEffect(() => {
    if (form === null) return;
    const outside = (e: MouseEvent) => {
      const box = formRef.current;
      if (box === null || !(e.target instanceof Node) || box.contains(e.target)) return;
      if (!mayLeave(formDirty)) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      setForm(null);
      setFormDirty(false);
    };
    document.addEventListener('click', outside, true);
    return () => document.removeEventListener('click', outside, true);
  }, [form, formDirty]);

  // PB10: the arrhes arrived by bank transfer. Asked once, as « VAD faite » is.
  const markTransferReceived = async (r: ReservationView) => {
    const amount = r.vadAmountCents === null ? '' : ` de ${fmtEuros(r.vadAmountCents)}`;
    if (!window.confirm(`Virement d’arrhes${amount} reçu pour ${r.guest?.lastName ?? 'ce client'} ?`)) return;
    setErr('');
    const result = await servicesFor(r.propertyCode).reservations.markTransferReceived(r.id);
    if (!result.ok) setErr(result.error.message);
    else await load();
  };

  // A5 (§8.6): the desk has keyed the web booking's deposit into the bank VAD.
  // Asked once, because there is no undo short of a correction by an admin.
  const markVadDone = async (r: ReservationView) => {
    const amount = r.vadAmountCents === null ? '' : ` de ${fmtEuros(r.vadAmountCents)}`;
    if (!window.confirm(`Arrhes${amount} saisies dans la VAD pour ${r.guest?.lastName ?? 'ce client'} ?`)) return;
    setErr('');
    const result = await servicesFor(r.propertyCode).reservations.markVadDone(r.id);
    if (!result.ok) setErr(result.error.message);
    else await load();
  };

  const transition = async (r: ReservationView, action: PmsTransitionAction) => {
    const who = r.guest?.lastName ?? 'ce client';
    let reason: string | null = null;

    // Asked for BEFORE the round trip, but never trusted as the enforcement:
    // migration 0077 refuses the action outright without one. This prompt only
    // saves the operator a failed call and a French error they would then have
    // to satisfy anyway.
    if (pmsActionNeedsReason(action)) {
      const typed = window.prompt(`${PMS_ACTION_LABELS_FR[action]} — ${who}\n\nMotif (obligatoire) :`, '');
      if (typed === null) return; // cancelled the prompt
      reason = typed.trim();
      if (reason === '') {
        setErr(`${PMS_ACTION_LABELS_FR[action]} : un motif est obligatoire.`);
        return;
      }
    } else if (action === 'cancel' || action === 'no_show') {
      setAct({ action, booking: r, stay: null });
      return;
    }

    setErr('');
    const result = await servicesFor(r.propertyCode).reservations.transition(r.id, action, reason);
    if (!result.ok) setErr(result.error.message);
    await load();
  };

  // Assignment names a LOGEMENT, never a booking. `assignRoom` still exists and
  // still works for a single-room booking, but it refuses one holding several
  // (0090) — so calling the room-level RPC uniformly means the list never has
  // two paths, one of which only fails on the bookings this increment added.
  const assignRoomStay = async (stay: RoomStayView, roomId: string) => {
    setErr('');
    const result = await servicesFor(stay.propertyCode).reservations.assignRoomStay(
      stay.id,
      roomId === '' ? null : Number(roomId),
    );
    if (!result.ok) setErr(result.error.message);
    await load();
  };

  // The state machine as it really is since 0091: an act on ONE room. The
  // booking-level `transition` above stays for the commercial acts, which fan
  // out across every logement.
  const transitionStay = async (
    stay: RoomStayView,
    booking: ReservationView,
    action: PmsTransitionAction,
  ) => {
    const who = stay.occupantName ?? booking.guest?.lastName ?? 'ce client';
    let reason: string | null = null;
    if (pmsActionNeedsReason(action)) {
      const typed = window.prompt(`${PMS_ACTION_LABELS_FR[action]} — ${who}\n\nMotif (obligatoire) :`, '');
      if (typed === null) return;
      reason = typed.trim();
      if (reason === '') {
        setErr(`${PMS_ACTION_LABELS_FR[action]} : un motif est obligatoire.`);
        return;
      }
    } else if (action === 'cancel' || action === 'no_show') {
      setAct({ action, booking, stay });
      return;
    }
    setErr('');
    const result = await servicesFor(stay.propertyCode).reservations.transitionRoomStay(stay.id, action, reason);
    if (!result.ok) setErr(result.error.message);
    await load();
  };

  // OB3: the dialog's confirm. A cancellation is booking-wide and carries the
  // reason and the fee; a no-show is the room's act (or the booking's, with one room).
  const runAct = async (choice: CancelChoice): Promise<string | null> => {
    if (act === null) return null;
    const svc = servicesFor(act.booking.propertyCode).reservations;
    const result =
      act.stay !== null
        ? await svc.transitionRoomStay(act.stay.id, act.action, null)
        : await svc.transition(act.booking.id, act.action, choice.reason, choice.reasonCode, choice.fee);
    if (!result.ok) return result.error.message;
    setAct(null);
    await load();
    return null;
  };

  const callServices = callFor === null ? null : servicesFor(callFor);
  if (callFor !== null && callServices !== null) {
    return (
      <div className="pms">
        <CallFlow
          propertyCode={callFor}
          services={callServices}
          reservations={(reservations ?? []).filter((r) => r.propertyCode === callFor)}
          onClose={() => setCallFor(null)}
          onSaved={() => void load()}
        />
      </div>
    );
  }

  return (
    <div className="pms">
      <div className="pms-head">
        <h2 className="section-title">Réservations — PMS (mode miroir)</h2>
        <span className="pms-hint">
          Saisie manuelle en parallèle de Vega/Amenitiz (P1) — la bascule Channex viendra en P2
        </span>
        <button type="button" className="auth-btn" onClick={openNew}>
          + Nouvelle réservation
        </button>
        {reach.map((b) => (
          <button key={b.property.code} type="button" className="auth-btn" onClick={() => setCallFor(b.property.code)}>
            {callButtonLabel(b.property)}
          </button>
        ))}
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}

      {act !== null && (
        <CancelDialog
          action={act.action}
          booking={act.booking}
          stays={act.stay !== null ? [act.stay] : (staysOf.get(act.booking.id) ?? [])}
          paidCents={paid === null || paid === 'off' ? 0 : (paid.get(act.booking.id)?.paidCents ?? 0)}
          today={todayIso}
          reservations={servicesFor(act.booking.propertyCode).reservations}
          onConfirm={runAct}
          onClose={() => setAct(null)}
        />
      )}

      {folioFor !== null && (
        <FolioDialog
          booking={folioFor}
          reservations={servicesFor(folioFor.propertyCode).reservations}
          invoicing={servicesFor(folioFor.propertyCode).invoicing}
          payments={servicesFor(folioFor.propertyCode).payments}
          onClose={() => setFolioFor(null)}
          onChanged={() => void load()}
        />
      )}

      <PendingConfirmations />
      <Turnaways />
      <TrainingReset onDone={() => void load()} />
      <DayPassage onDone={() => void load()} />

      <div className="pms-toolbar">
        <div className="pms-chips">
          {(Object.keys(PMS_FILTER_LABELS) as PmsFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              className={`pms-chip ${filter === f ? 'is-active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {PMS_FILTER_LABELS[f]}
              {f === 'a_traiter' && overdueCount > 0 ? ` (${overdueCount})` : ''}
            </button>
          ))}
          <button
            type="button"
            className={`pms-chip ${pmsOnly ? 'is-active' : ''}`}
            aria-pressed={pmsOnly}
            title="Seulement les réservations faites dans le PMS — celles de Vega se changent dans Vega"
            onClick={() => setPmsOnly((v) => !v)}
          >
            Réservations PMS
          </button>
        </div>
        <input
          type="search"
          placeholder="Nom, n° OTA…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pms-search"
        />
      </div>

      {form && (
        <div ref={formRef}>
          <ReservationForm
            initial={form}
            roomTypes={roomTypes}
            ratePlans={ratePlans}
            guests={pms.guests}
            rooms={rooms}
            products={products}
            saving={saving}
            error={err}
            onSave={(f: FormState, code: string | null) => void save(f, code)}
            propertyCode={formProperty}
            etablissementLabels={etablissementLabels}
            onCancel={closeForm}
            onDirtyChange={setFormDirty}
            advance={formAdvance}
            planGap={formPlanGap}
          billed={formBilled}
          journal={formJournal}
            canSeeNeeds={canNeeds}
            vegaHeld={formHeld}
          />
        </div>
      )}

      <div className="pms-table-wrap">
        <table className="pms-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Séjour</th>
              <th>Cat.</th>
              <th>Chambre</th>
              <th>Pers.</th>
              <th>Source</th>
              <th>Total</th>
              {showPay && <th title="Ce qui a été payé sur la réservation (arrhes et autres règlements) comparé à son total">Paiement</th>}
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reservations === null && (
              <tr>
                <td colSpan={cols} className="pms-empty">
                  Chargement…
                </td>
              </tr>
            )}
            {reservations !== null && visible.length === 0 && (
              <tr>
                <td colSpan={cols} className="pms-empty">
                  Aucune réservation dans cette vue.
                </td>
              </tr>
            )}
            {visible.map((r) => {
              const mine = staysOf.get(r.id) ?? [];
              // A booking whose logements have not loaded is drawn from its own
              // derived columns rather than as an empty row — the read may be
              // in flight, and a blank « Chambre » would look like a bug.
              const multi = pmsLiveRoomCount(mine) > 1;
              const partial = pmsPartialStatusLabelFr(mine);
              // OB1/OB2: a Vega-held booking offers no act here — the seed would
              // undo it within 30 minutes, and the database refuses it anyway.
              const held = rowIsVegaHeld(modes, r);
              const overdue = bookingOverdue(r, staysOf.get(r.id), todayIso, modes);
              const roomSelect = (stay: RoomStayView) => {
                const typeRooms = rooms.filter((room) => room.roomTypeId === stay.roomTypeId);
                return pmsIsEditable(stay.status) && !held ? (
                  <select
                    value={stay.roomId === null ? '' : String(stay.roomId)}
                    onChange={(e) => void assignRoomStay(stay, e.target.value)}
                  >
                    <option value="">—</option>
                    {typeRooms.map((room) => (
                      <option key={room.id} value={room.id}>
                        {room.roomNumber}
                      </option>
                    ))}
                  </select>
                ) : (
                  (stay.roomNumber ?? '—')
                );
              };
              const only = mine.length === 1 ? mine[0]! : null;
              return (
                <Fragment key={r.id}>
                  <tr className={`pms-row-${r.status}`}>
                    <td>
                      <strong>
                        {(r.guest?.lastName ?? '?').toUpperCase()} {r.guest?.firstName ?? ''}
                      </strong>
                      <EtablissementTag code={r.propertyCode} />
                      {r.channelRef && <div className="pms-sub">{r.channel} · {r.channelRef}</div>}
                    </td>
                    <td>
                      {fmtDate(r.checkinDate)} → {fmtDate(r.checkoutDate)}
                      <div className="pms-sub">
                        {pmsNightCount(r.checkinDate, r.checkoutDate)} nuit
                        {pmsNightCount(r.checkinDate, r.checkoutDate) > 1 ? 's' : ''}
                      </div>
                    </td>
                    {/* On a multi-room booking the category and the unit are
                        the FIRST logement's, which is a derived stand-in and
                        not a fact about the booking (0090). So they are
                        replaced by a count, and the rooms are listed below. */}
                    <td>{multi ? `${mine.length} logements` : typeById.get(r.roomTypeId)?.code ?? r.roomTypeId}</td>
                    <td>{multi ? '—' : only !== null ? roomSelect(only) : (r.roomNumber ?? '—')}</td>
                    <td>
                      {r.adults}A{r.children > 0 ? ` + ${r.children}E` : ''}
                    </td>
                    <td>{PMS_SOURCE_LABELS_FR[r.source]}</td>
                    <td className="pms-num">{fmtEuros(r.totalCents)}</td>
                    {showPay && <td>{paymentCell(r)}</td>}
                    <td>
                      <span className={`pms-status pms-status-${r.status}`}>
                        {/* « Partiellement arrivée » — the label 0091
                            deliberately did not make a status. Null unless the
                            booking is genuinely split, so a single-room row
                            reads exactly as it did. */}
                        {partial ?? PMS_STATUS_LABELS_FR[r.status]}
                        {r.noShow ? ' (no-show)' : ''}
                      </span>
                      {held && (
                        <span className="pms-tag" title={VEGA_HELD_TITLE_FR}>
                          Vega
                        </span>
                      )}
                      {overdue !== null && (
                        <span
                          className="pms-tag is-overdue"
                          title="Dates passées : à régler (arrivée tardive, no-show, annulation, ou départ)"
                        >
                          {OVERDUE_LABELS_FR[overdue]}
                        </span>
                      )}
                      {r.guarantee === 'vad_pending' && r.status !== 'cancelled' && (
                        <span className="pms-tag is-vad" title="Arrhes à saisir dans la VAD de la banque">
                          VAD {r.vadAmountCents === null ? 'à traiter' : fmtEuros(r.vadAmountCents)}
                        </span>
                      )}
                      {r.guarantee === 'transfer_pending' && r.status !== 'cancelled' && (
                        <span
                          className={`pms-tag ${transferOverdue(r.transferDueOn ?? null, todayIso) ? 'is-overdue' : 'is-transfer'}`}
                          title="Arrhes à recevoir par virement : la réception relance ou annule, rien ne s’annule seul"
                        >
                          {transferOverdue(r.transferDueOn ?? null, todayIso) ? 'Virement en retard' : 'Virement'}{' '}
                          {r.vadAmountCents === null ? '' : fmtEuros(r.vadAmountCents)}
                          {r.transferDueOn ? ` · avant le ${fmtDate(r.transferDueOn)}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="pms-actions">
                      {held ? (
                        <button type="button" className="pms-mini" title={VEGA_HELD_TITLE_FR} onClick={() => void openEdit(r)}>
                          Voir
                        </button>
                      ) : (
                        pmsIsEditable(r.status) && (
                          <button type="button" className="pms-mini" onClick={() => void openEdit(r)}>
                            ✎
                          </button>
                        )
                      )}
                      {/* OB6: the PMS's own booking — its note, payments and factures. */}
                      {!held && (
                        <button type="button" className="pms-mini" onClick={() => setFolioFor(r)}>
                          Note
                        </button>
                      )}
                      {/* On a multi-room booking only the COMMERCIAL acts are
                          offered here: the physical ones belong to a room and
                          the RPC refuses them at this level (0091), so showing
                          them would be offering a button that cannot work. */}
                      {r.guarantee === 'vad_pending' && r.status !== 'cancelled' && (
                        <button type="button" className="pms-mini" onClick={() => void markVadDone(r)}>
                          VAD faite
                        </button>
                      )}
                      {r.guarantee === 'transfer_pending' && r.status !== 'cancelled' && (
                        <button type="button" className="pms-mini" onClick={() => void markTransferReceived(r)}>
                          Virement reçu
                        </button>
                      )}
                      {(held ? [] : pmsActionsFrom(r.status))
                        .filter((a) => !multi || pmsActionIsBookingWide(a))
                        .map((a) => (
                          <button key={a} type="button" className="pms-mini" onClick={() => void transition(r, a)}>
                            {PMS_ACTION_LABELS_FR[a]}
                          </button>
                        ))}
                    </td>
                  </tr>
                  {multi &&
                    mine.map((stay, i) => (
                      <tr key={stay.id} className="pms-row-stay">
                        <td className="pms-stay-label">
                          ↳ Logement {i + 1}
                          {stay.occupantName !== null && <div className="pms-sub">{stay.occupantName}</div>}
                        </td>
                        <td>
                          {fmtDate(stay.checkinDate)} → {fmtDate(stay.checkoutDate)}
                          <div className="pms-sub">
                            {pmsNightCount(stay.checkinDate, stay.checkoutDate)} nuit
                            {pmsNightCount(stay.checkinDate, stay.checkoutDate) > 1 ? 's' : ''}
                          </div>
                        </td>
                        <td>{typeById.get(stay.roomTypeId)?.code ?? stay.roomTypeId}</td>
                        <td>{roomSelect(stay)}</td>
                        <td>
                          {stay.adults}A{stay.children > 0 ? ` + ${stay.children}E` : ''}
                          {stay.infants > 0 ? ` + ${stay.infants}B` : ''}
                        </td>
                        <td />
                        <td />
                        {showPay && <td />}
                        <td>
                          <span className={`pms-status pms-status-${stay.status}`}>
                            {PMS_STATUS_LABELS_FR[stay.status]}
                          </span>
                        </td>
                        <td className="pms-actions">
                          {/* The physical acts, where they belong. */}
                          {(held ? [] : pmsActionsFrom(stay.status))
                            .filter((a) => !pmsActionIsBookingWide(a))
                            .map((a) => (
                              <button
                                key={a}
                                type="button"
                                className="pms-mini"
                                onClick={() => void transitionStay(stay, r, a)}
                              >
                                {PMS_ACTION_LABELS_FR[a]}
                              </button>
                            ))}
                        </td>
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
